/**
 * Wearing the host site's colours (P2-124, ADR-0248).
 *
 * The loader reads the page's computed colours — it is the only axon code that
 * can — and hands them over raw (`HostPalette` in loader/protocol.ts). This file
 * turns those strings into the panel's custom properties. Pure: no DOM, no
 * globals, so every decision below is a function a test can call.
 *
 * A BOUNDARY, TWICE OVER. The strings come from a page we do not control, over
 * postMessage. Nothing here assigns a received string anywhere: each one is
 * parsed into three bytes, and every value this file emits is a `#rrggbb` it
 * formatted itself. A string that is not a colour we recognise is dropped.
 *
 * WHAT IS DECIDED HERE.
 *
 *   accent   The site's brand colour: `<meta name="theme-color">` if it is a
 *            real colour, else the commonest button fill, else the commonest
 *            link colour. "Real" means chromatic — white, black and greys are a
 *            site's canvas, not its brand, and a grey header would be a worse
 *            widget than our indigo.
 *   page     The page's own background and text, when the background is opaque
 *            and the pair is readable (4.5:1). Used only under the `auto` theme.
 *   scheme   Light or dark, from the page background, for `auto`.
 *
 * And then every colour the panel derives from those — hover, text on the
 * accent, muted text, borders — is computed with a WCAG contrast check, because
 * the inputs are whatever a site happens to use and the output has to be legible
 * for a visitor who never chose any of it.
 *
 * NO COLOUR LITERALS (axon/no-color-literal). The only fixed colours here are
 * the white and black poles of the contrast formula and the two default panel
 * surfaces, written as channel triples: they are arithmetic, not paint.
 */
import type { HostPalette } from '../loader/protocol';

import type { EffectiveTheme } from './theme';

/** One sRGB colour, channels 0–255. */
export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };
/** The panel's default raised surface in each scheme — `--ow-surface` in widget.css. */
const DEFAULT_SURFACE: Record<EffectiveTheme, Rgb> = {
  light: WHITE,
  dark: { r: 31, g: 32, b: 35 },
};

/** Long enough for any colour `getComputedStyle` writes; short enough to bound work. */
const MAX_VALUE_LENGTH = 64;
/** The loader sends at most sixteen of each; anything longer is not our loader. */
const MAX_SAMPLES = 16;

// ---------------------------------------------------------------------------
// Reading the message
// ---------------------------------------------------------------------------

function sample(value: unknown): string | null {
  return typeof value === 'string' && value.length <= MAX_VALUE_LENGTH ? value : null;
}

function samples(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .slice(0, MAX_SAMPLES)
    .map(sample)
    .filter((item): item is string => item !== null);
}

/**
 * The `palette` field of an `init`, narrowed. Absent or malformed → null, which
 * is the same as an old loader that never sent one: the panel keeps its defaults.
 */
export function readHostPalette(raw: unknown): HostPalette | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  return {
    m: sample(record['m']),
    bg: samples(record['bg']),
    fg: sample(record['fg']) ?? '',
    btn: samples(record['btn']),
    a: samples(record['a']),
  };
}

// ---------------------------------------------------------------------------
// Colour arithmetic
// ---------------------------------------------------------------------------

/** A parsed colour with its opacity, 0–1. */
interface Rgba extends Rgb {
  readonly alpha: number;
}

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/iu;
/** `rgb(1, 2, 3)`, `rgba(1, 2, 3, 0.5)`, `rgb(1 2 3 / 50%)` — what browsers compute. */
const RGB_FUNCTION =
  /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/iu;
/** `color(srgb 0.1 0.2 0.3 / 0.5)` — what Chromium computes for some modern colour syntax. */
const SRGB_FUNCTION =
  /^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+)(%?))?\s*\)$/iu;

function channel(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function alphaOf(value: string | undefined, percent: string | undefined): number {
  if (value === undefined) {
    return 1;
  }
  const parsed = Number(value) / (percent === '%' ? 100 : 1);
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 0;
}

/**
 * Hex, `rgb()`/`rgba()` and `color(srgb …)`; anything else is null.
 *
 * Deliberately not a CSS parser. Computed values come back in these shapes for
 * every colour authored in hex, a name, `rgb()` or `hsl()`; a site written in
 * `oklch()` may compute to something else, and then this file finds no accent
 * and the widget keeps its own — a smaller wrong than guessing.
 */
export function parseColour(text: string | null): Rgba | null {
  if (text === null) {
    return null;
  }
  const value = text.trim();

  const hex = HEX.exec(value);
  if (hex !== null) {
    const digits = hex[1] ?? '';
    const full =
      digits.length <= 4
        ? digits
            .split('')
            .map((digit) => digit + digit)
            .join('')
        : digits;
    const byte = (index: number): number => Number.parseInt(full.slice(index, index + 2), 16);
    return {
      r: byte(0),
      g: byte(2),
      b: byte(4),
      alpha: full.length === 8 ? byte(6) / 255 : 1,
    };
  }

  const rgb = RGB_FUNCTION.exec(value);
  if (rgb !== null) {
    return {
      r: channel(Number(rgb[1])),
      g: channel(Number(rgb[2])),
      b: channel(Number(rgb[3])),
      alpha: alphaOf(rgb[4], rgb[5]),
    };
  }

  const srgb = SRGB_FUNCTION.exec(value);
  if (srgb !== null) {
    return {
      r: channel(Number(srgb[1]) * 255),
      g: channel(Number(srgb[2]) * 255),
      b: channel(Number(srgb[3]) * 255),
      alpha: alphaOf(srgb[4], srgb[5]),
    };
  }
  return null;
}

/** An opaque colour, or null. Half-transparent paint is not a colour we can match. */
function opaque(text: string | null): Rgb | null {
  const colour = parseColour(text);
  return colour !== null && colour.alpha >= 0.99 ? { r: colour.r, g: colour.g, b: colour.b } : null;
}

export function toHex(colour: Rgb): string {
  const pair = (value: number): string => channel(value).toString(16).padStart(2, '0');
  return `#${pair(colour.r)}${pair(colour.g)}${pair(colour.b)}`;
}

/** WCAG 2 relative luminance. */
export function luminance(colour: Rgb): number {
  const linear = (value: number): number => {
    const c = value / 255;
    return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(colour.r) + 0.7152 * linear(colour.g) + 0.0722 * linear(colour.b);
}

/** WCAG 2 contrast ratio, 1–21. */
export function contrast(a: Rgb, b: Rgb): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** `from` moved `amount` (0–1) of the way to `to`, in sRGB — what `color-mix(in srgb)` does. */
export function mix(from: Rgb, to: Rgb, amount: number): Rgb {
  return {
    r: channel(from.r + (to.r - from.r) * amount),
    g: channel(from.g + (to.g - from.g) * amount),
    b: channel(from.b + (to.b - from.b) * amount),
  };
}

/**
 * The smallest step from `from` towards `to` that reaches `ratio` against
 * `against`; `to` itself if no step does. Used to darken a too-light accent, or
 * to find the faintest muted text that is still readable.
 */
function towardsContrast(from: Rgb, to: Rgb, against: Rgb, ratio: number): Rgb {
  for (let step = 0; step <= 20; step += 1) {
    const candidate = mix(from, to, step / 20);
    if (contrast(candidate, against) >= ratio) {
      return candidate;
    }
  }
  return to;
}

/**
 * Colourful enough to be a brand, not a canvas: HSL saturation ≥ 0.25 and
 * lightness inside 0.12–0.9. Pure white, black, greys and near-greys fail.
 */
export function isChromatic(colour: Rgb): boolean {
  const max = Math.max(colour.r, colour.g, colour.b) / 255;
  const min = Math.min(colour.r, colour.g, colour.b) / 255;
  const lightness = (max + min) / 2;
  if (lightness < 0.12 || lightness > 0.9 || max === min) {
    return false;
  }
  const saturation = (max - min) / (1 - Math.abs(2 * lightness - 1));
  return saturation >= 0.25;
}

/** Whether light text reads better on this background than dark text. */
function isDark(colour: Rgb): boolean {
  return contrast(colour, WHITE) > contrast(colour, BLACK);
}

// ---------------------------------------------------------------------------
// What the site looks like
// ---------------------------------------------------------------------------

/** The chromatic colour that occurs most often in `values`; the earliest wins a tie. */
function commonest(values: readonly string[]): Rgb | null {
  const counts = new Map<string, { colour: Rgb; count: number }>();
  for (const value of values) {
    const colour = opaque(value);
    if (colour === null || !isChromatic(colour)) {
      continue;
    }
    const key = toHex(colour);
    const entry = counts.get(key);
    counts.set(key, { colour, count: (entry?.count ?? 0) + 1 });
  }
  let best: { colour: Rgb; count: number } | null = null;
  for (const entry of counts.values()) {
    if (best === null || entry.count > best.count) {
      best = entry;
    }
  }
  return best?.colour ?? null;
}

export interface SiteColours {
  /** The site's brand colour, or null when it shows none. */
  readonly accent: Rgb | null;
  /** The page's background and text, when opaque and readable together. */
  readonly page: { readonly background: Rgb; readonly text: Rgb } | null;
  /** Light or dark by the page background; null when the background is unknown. */
  readonly pageIsDark: boolean | null;
}

export const NO_SITE_COLOURS: SiteColours = { accent: null, page: null, pageIsDark: null };

export function readSiteColours(palette: HostPalette | null): SiteColours {
  if (palette === null) {
    return NO_SITE_COLOURS;
  }

  const meta = opaque(palette.m);
  const accent =
    (meta !== null && isChromatic(meta) ? meta : null) ??
    commonest(palette.btn) ??
    commonest(palette.a);

  // `<body>` paints over `<html>`, so the LAST opaque background is the visible one.
  const background = palette.bg
    .map(opaque)
    .reduce<Rgb | null>((visible, colour) => colour ?? visible, null);
  const text = opaque(palette.fg);

  return {
    accent,
    page:
      background !== null && text !== null && contrast(background, text) >= 4.5
        ? { background, text }
        : null,
    pageIsDark: background === null ? null : isDark(background),
  };
}

// ---------------------------------------------------------------------------
// The panel's custom properties
// ---------------------------------------------------------------------------

/**
 * Every property this file may write onto `<html>`. A property not in the
 * current result is REMOVED, so the stylesheet's own value comes back — that is
 * how switching from `auto` to `light` undoes the site's surfaces.
 */
export const PALETTE_PROPERTIES = [
  '--ow-bg',
  '--ow-surface',
  '--ow-surface-sunken',
  '--ow-border',
  '--ow-border-strong',
  '--ow-text',
  '--ow-text-muted',
  '--ow-text-subtle',
  '--ow-accent',
  '--ow-accent-hover',
  '--ow-accent-fg',
  '--ow-accent-surface',
  '--ow-accent-text',
  '--ow-focus',
] as const;

export type PaletteProperty = (typeof PALETTE_PROPERTIES)[number];
export type PaletteVariables = Partial<Record<PaletteProperty, string>>;

export interface PaletteInput {
  /** The accent to paint with: the brand's own, or the site's, or null for the default. */
  readonly accent: Rgb | null;
  /** The page's background and text — only under the `auto` theme. */
  readonly page: SiteColours['page'];
  /** The scheme actually painted, which picks the default surface. */
  readonly scheme: EffectiveTheme;
}

/**
 * The panel's colours, as custom properties.
 *
 * With `page`, the panel IS the page: its shell takes the page background, its
 * text the page text, and borders, muted text and sunken areas are mixed from the
 * two — muted text is the faintest mix that still reads at 4.5:1, subtle at 3:1.
 *
 * With `accent`, the header, buttons and focus ring take the brand colour; the
 * text on it is white or near-black, whichever reads better; the accent as TEXT
 * (a badge) is darkened or lightened until it reads on its own tint.
 */
export function paletteVariables(input: PaletteInput): PaletteVariables {
  const variables: PaletteVariables = {};
  const dark = input.scheme === 'dark';
  let surface = DEFAULT_SURFACE[input.scheme];
  let text: Rgb = dark ? WHITE : BLACK;

  if (input.page !== null) {
    const { background, text: pageText } = input.page;
    surface = dark ? mix(background, pageText, 0.05) : mix(background, WHITE, 0.7);
    text = pageText;
    variables['--ow-bg'] = toHex(background);
    variables['--ow-surface'] = toHex(surface);
    variables['--ow-surface-sunken'] = toHex(
      dark ? mix(background, BLACK, 0.2) : mix(background, pageText, 0.035),
    );
    variables['--ow-border'] = toHex(mix(background, pageText, 0.22));
    variables['--ow-border-strong'] = toHex(mix(background, pageText, 0.36));
    variables['--ow-text'] = toHex(pageText);
    variables['--ow-text-muted'] = toHex(
      towardsContrast(mix(background, pageText, 0.7), pageText, background, 4.5),
    );
    variables['--ow-text-subtle'] = toHex(
      towardsContrast(mix(background, pageText, 0.55), pageText, background, 3),
    );
  }

  if (input.accent !== null) {
    const accent = input.accent;
    const foreground = contrast(accent, WHITE) >= contrast(accent, BLACK) ? WHITE : BLACK;
    const tint = mix(surface, accent, dark ? 0.2 : 0.1);
    const accentText = towardsContrast(accent, text, tint, 4.5);
    variables['--ow-accent'] = toHex(accent);
    variables['--ow-accent-hover'] = toHex(mix(accent, foreground === WHITE ? BLACK : WHITE, 0.12));
    // Near-black rather than black: the default token is not pure either.
    variables['--ow-accent-fg'] = toHex(foreground === WHITE ? WHITE : mix(BLACK, WHITE, 0.07));
    variables['--ow-accent-surface'] = toHex(tint);
    variables['--ow-accent-text'] = toHex(accentText);
    variables['--ow-focus'] = toHex(towardsContrast(accent, text, surface, 3));
  }
  return variables;
}

/**
 * The launcher's fill. It sits on the CUSTOMER's page with a white glyph that
 * the loader cannot recolour, so a light accent (a yellow, a mint) is darkened
 * until the glyph reads at 3:1 — the WCAG floor for a control's graphics.
 */
export function launcherColour(accent: Rgb): string {
  return toHex(towardsContrast(accent, BLACK, WHITE, 3));
}

/** The subset of `CSSStyleDeclaration` this file writes through. */
export interface PaletteTarget {
  setProperty(property: string, value: string): void;
  removeProperty(property: string): string;
}

/** Writes the result onto `<html>`'s inline style; clears every property it did not set. */
export function applyPalette(style: PaletteTarget, variables: PaletteVariables): void {
  for (const property of PALETTE_PROPERTIES) {
    const value = variables[property];
    if (value === undefined) {
      style.removeProperty(property);
    } else {
      style.setProperty(property, value);
    }
  }
}
